import { User } from "lucide-react";

// Occupants are exactly the guests already attached to each stay (name + lead flag). No extra personal fields exist or are invented.
export function OccupantCards({ stays }) {
  const occupants = stays.flatMap((s) => s.guests.map((g) => ({ ...g, stay: s })));
  return (
    <section data-testid="booking-occupants" className="rounded-[10px] border border-jigsaw-border bg-white">
      <header className="flex items-center justify-between px-5 pt-3.5 pb-2.5"><h2 className="text-[16px] font-semibold text-jigsaw-navy">Occupants</h2><span className="text-[12px] text-jigsaw-muted">{occupants.length} recorded across {stays.length} {stays.length === 1 ? "stay" : "stays"}</span></header>
      <div className="px-5 pb-4">
        {occupants.length === 0 ? <p data-testid="booking-occupants-empty" className="text-sm text-jigsaw-muted">No guests have been recorded against these stays yet.</p> : (
          <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {occupants.map((o) => (
              <li key={o.id} data-testid={`occupant-card-${o.id}`} className="flex items-start gap-3 rounded-[10px] border border-jigsaw-border p-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-jigsaw-navy text-white"><User className="h-4 w-4" /></span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2"><p className="min-w-0 truncate text-[14px] font-semibold text-jigsaw-navy">{o.full_name}</p>{o.is_lead_guest && <span className="shrink-0 rounded-full bg-jigsaw-orange/10 px-2 py-0.5 text-[11px] font-semibold text-jigsaw-orange">Lead guest</span>}</div>
                  <p className="truncate text-[12px] text-jigsaw-muted">{o.stay.property_name}</p>
                  <p className="font-mono-ref text-[12px] text-jigsaw-muted">{o.stay.check_in_date} → {o.stay.check_out_date}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// Sums the API's Decimal strings in integer pence (no floats), only over the statuses the API itself counts toward the balance.
const pence = (v) => { const [w, f = ""] = String(v).replace(/^-/, "").split("."); const n = parseInt(w, 10) * 100 + parseInt((f + "00").slice(0, 2), 10); return String(v).startsWith("-") ? -n : n; };
const fmt = (p, ccy) => `${p < 0 ? "-" : ""}${ccy === "GBP" ? "£" : ccy + " "}${(Math.abs(p) / 100).toFixed(2)}`;

export function LedgerSplit({ ledger }) {
  const counted = ledger.items.filter((e) => ledger.balance_basis.statuses.includes(e.status));
  const byType = {};
  for (const e of counted) { const k = `${e.entry_type}|${e.currency}`; byType[k] = (byType[k] || 0) + pence(e.amount); }
  const rows = Object.entries(byType).map(([k, v]) => { const [type, ccy] = k.split("|"); return { type, ccy, v }; });
  return (
    <div data-testid="booking-ledger-split" className="mt-4 border-t border-jigsaw-border pt-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-jigsaw-muted">Ledger split</p>
      {rows.length === 0 ? <p className="mt-1 text-[13px] text-jigsaw-muted">No recorded or reconciled ledger entries yet.</p> : (
        <dl className="mt-1 space-y-1">
          {rows.map((r) => <div key={r.type + r.ccy} className="flex items-center justify-between text-[13px]"><dt className="text-jigsaw-muted">{r.type.charAt(0) + r.type.slice(1).toLowerCase()}s</dt><dd className="font-mono-ref text-jigsaw-navy">{fmt(r.v, r.ccy)}</dd></div>)}
        </dl>
      )}
      <p className="mt-2 text-[11px] text-jigsaw-muted">{counted.length} of {ledger.items.length} entries count towards the balance ({ledger.balance_basis.statuses.join(", ").toLowerCase()}).</p>
    </div>
  );
}
